import React, { useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import TablePagination from '@mui/material/TablePagination'
import Checkbox from '@mui/material/Checkbox'
import { CircularProgress, Tooltip } from '@mui/material'
import { ExpandMore, ExpandLess, Check, Close } from '@mui/icons-material'

// Third-party Imports
import classnames from 'classnames'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/react-table'

// Component Imports
import TablePaginationComponent from '../molecules/TablePaginationComponent'

// Style Imports
import tableStyles from '../../styles/table.module.scss'

// Column Definitions
const columnHelper = createColumnHelper()

const CampaignPreparationTable = (props) => {
  const {
    data,
    rowSelection,
    setRowSelection,
    fetching,
    fuzzyFilter,
    globalFilter,
    setGlobalFilter,
    pageSize,
    disableInteraction = false
  } = props
  const { t } = useTranslation()

  const skipPageResetRef = useRef()

  const hasExplanation = useMemo(() => {
    return data.length > 0 && data.some(item => 'explanation' in item)
  }, [data])

  const columns = useMemo(
    () => {
      const columns = [
        {
          id: 'select',
          header: ({ table }) => (
            <Checkbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
                disabled: disableInteraction
              }}
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect() || disableInteraction,
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler()
              }}
            />
          )
        },

        columnHelper.accessor('name', {
          header: t('mailTemplate.wizard.firmList.firmName'),
          meta: { flexGrow: 1 },
          cell: ({ row }) => {
            const firmName = row.original.name
            return (
              <div className="flex items-center gap-4" style={{ width: '100%', minWidth: '120px' }}>
                <div className="flex flex-col" style={{ width: '100%' }}>
                  <Typography color="text.primary" className="font-medium">
                    <Tooltip
                      title={firmName}
                      arrow
                      placement="top"
                      disableHoverListener={firmName.length <= 40}
                    >
                      <span style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        maxWidth: '300px'
                      }}>
                        {firmName}
                      </span>
                    </Tooltip>
                  </Typography>
                </div>
              </div>
            )
          }
        }),

        columnHelper.accessor('isRelevant', {
          header: t('mailTemplate.wizard.firmList.relevant'),
          cell: ({ row }) => (
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                {row.original.isRelevant === true
                  ? <Check style={{ color: 'rgba(76, 175, 80, 1)' }}/>
                  : row.original.isRelevant === false
                    ? <Close style={{ color: 'rgba(244, 67, 54, 1)' }}/>
                    : <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
                }
              </div>
            </div>
          )
        })
      ]

      if (hasExplanation) {
        columns.push(
          columnHelper.accessor('explanation', {
            header: t('mailTemplate.wizard.firmList.explanation'),
            meta: { flexGrow: 2 },
            cell: ({ row }) => {
              const explanation = row.original.explanation

              const decodedExplanation = explanation
                ? explanation.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, '\'').replace(/&nbsp;/g, ' ')
                : explanation
              return (
                <div className="flex items-center gap-4">
                  <div className="flex flex-col" style={{ width: '100%', minWidth: '200px' }}>
                    {decodedExplanation
                      ? (
                        <Typography color="text.primary" className="font-medium">
                          <Tooltip
                            title={
                              <div style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 10,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'pre-wrap'
                              }}>
                                {decodedExplanation}
                              </div>
                            }
                            arrow
                            placement="top"
                            disableHoverListener={decodedExplanation.length <= 80}
                          >
                          <span style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            maxWidth: '470px'
                          }}>
                            {decodedExplanation}
                          </span>
                          </Tooltip>
                        </Typography>
                        )
                      : (
                        <Typography variant="body2" style={{ color: 'rgba(158, 158, 158, 1)' }}>—</Typography>
                        )}
                  </div>
                </div>
              )
            }
          })
        )
      }

      return columns
    },
    [t, hasExplanation, disableInteraction]
  )

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      if (a.isRelevant === true && a.isRelevant !== b.isRelevant) return -1
      if (b.isRelevant === true && a.isRelevant !== b.isRelevant) return 1
      return 0
    })
  }, [data])

  const table = useReactTable({
    data: sortedData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter
    },
    state: {
      rowSelection,
      globalFilter
    },
    initialState: {
      pagination: {
        pageSize: 10
      }
    },
    enableRowSelection: !disableInteraction,
    globalFilterFn: fuzzyFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    autoResetPageIndex: !skipPageResetRef.current,
    autoResetExpanded: !skipPageResetRef.current,
    getRowId: (row) => row.id
  })

  useEffect(() => {
    skipPageResetRef.current = false
  })

  useEffect(() => {
    table.setPageSize(pageSize)
  }, [pageSize, table])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', height: '100%' }}>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="overflow-x-auto" style={{ flex: 1 }}>
          <table className={tableStyles.table}>
            <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          className={classnames({
                            'flex items-center': header.column.getIsSorted(),
                            'cursor-pointer select-none':
                              header.column.getCanSort()
                          })}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: <ExpandLess/>,
                            desc: <ExpandMore/>
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                        )}
                  </th>
                ))}
              </tr>
            ))}
            </thead>
            <tbody>
            {table.getFilteredRowModel().rows.length === 0
              ? (
                <tr>
                  <td
                    colSpan={table.getVisibleFlatColumns().length}
                    className="text-center"
                  >
                    {fetching
                      ? (
                        <CircularProgress/>
                        )
                      : (
                          t('mailTemplate.wizard.firmList.noData')
                        )}
                  </td>
                </tr>
                )
              : (
                  table.getRowModel().rows.slice(0, table.getState().pagination.pageSize).map((row) => {
                    return (
                    <tr
                      key={row.id}
                      className={classnames({ selected: row.getIsSelected() })}
                      style={{
                        cursor: disableInteraction ? 'default' : 'pointer'
                      }}
                      onClick={disableInteraction ? undefined : row.getToggleSelectedHandler()}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                    )
                  })
                )}
            </tbody>
          </table>
        </div>

        <div style={{ flexShrink: 0, marginTop: 'auto' }}>
          <TablePagination
            component={() => (
              <TablePaginationComponent
                table={table}
                infoText1={t('mailTemplate.wizard.firmList.tableInfo_1')}
                infoText2={t('mailTemplate.wizard.firmList.tableInfo_2')}
              />
            )}
            count={table.getFilteredRowModel().rows.length}
            rowsPerPage={table.getState().pagination.pageSize}
            page={table.getState().pagination.pageIndex}
            onPageChange={(_, page) => {
              table.setPageIndex(page)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default CampaignPreparationTable
